import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getBackendUrl, getImageUrl } from '../utils/api';
import useSectionTracking from '../hooks/useSectionTracking';
import { trackPageView, trackButtonClick } from '../utils/analytics';

const API = getBackendUrl();

// Logo URL - Fallen Dragon Logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_fallen-events-hub/artifacts/9a9pm8c7_Fallen%20Dragon%20Logo.jpeg";

// Single atmospheric dark forest image for continuous background
const FOREST_BG = "https://images.unsplash.com/photo-1573339607881-208e75e4b267?auto=format&fit=crop&w=1920&q=80";

// Navigation Component
// Video Logo URL (stored on R2)
const VIDEO_LOGO_URL = "https://images.fallendragonevents.co.uk/hero-video.mp4";

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const videoRef = React.useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Video loop with 10 second pause
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleVideoEnd = () => {
      setTimeout(() => {
        if (video) {
          video.currentTime = 0;
          video.play().catch(console.error);
        }
      }, 10000); // 10 second pause
    };

    const handleCanPlay = () => {
      video.play().catch(console.error);
    };

    video.addEventListener('ended', handleVideoEnd);
    video.addEventListener('canplay', handleCanPlay);
    
    return () => {
      video.removeEventListener('ended', handleVideoEnd);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setMenuOpen(false);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled ? 'bg-black/90 backdrop-blur-sm shadow-lg' : 'bg-gradient-to-b from-black/70 via-black/40 to-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-6 py-4 mt-2 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <video 
            ref={videoRef}
            autoPlay
            muted
            playsInline
            preload="auto"
            poster={LOGO_URL}
            className="h-12 w-auto rounded"
          >
            <source src={VIDEO_LOGO_URL} type="video/mp4" />
          </video>
        </Link>
        
        {/* Desktop Menu */}
        <div className="hidden md:flex items-center space-x-10">
          <button onClick={() => scrollToSection('about')} className="text-white hover:text-gray-200 transition-colors text-sm tracking-[0.2em] uppercase drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
            About
          </button>
          <button onClick={() => scrollToSection('events')} className="text-white hover:text-gray-200 transition-colors text-sm tracking-[0.2em] uppercase drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
            Events
          </button>
          <button onClick={() => scrollToSection('gallery')} className="text-white hover:text-gray-200 transition-colors text-sm tracking-[0.2em] uppercase drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
            Gallery
          </button>
          <button onClick={() => scrollToSection('contact')} className="text-white hover:text-gray-200 transition-colors text-sm tracking-[0.2em] uppercase drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
            Contact
          </button>
          <Link to="/general-rules" className="text-white hover:text-gray-200 transition-colors text-sm tracking-[0.2em] uppercase drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
            Rules
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button 
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden text-gray-300 p-2"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {menuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-black/95 backdrop-blur-sm border-t border-gray-800">
          <div className="px-6 py-4 space-y-4">
            <button onClick={() => scrollToSection('about')} className="block w-full text-left text-gray-300 hover:text-white py-2 tracking-wider">
              About
            </button>
            <button onClick={() => scrollToSection('events')} className="block w-full text-left text-gray-300 hover:text-white py-2 tracking-wider">
              Events
            </button>
            <button onClick={() => scrollToSection('gallery')} className="block w-full text-left text-gray-300 hover:text-white py-2 tracking-wider">
              Gallery
            </button>
            <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-gray-300 hover:text-white py-2 tracking-wider">
              Contact
            </button>
            <Link to="/general-rules" className="block w-full text-left text-gray-300 hover:text-white py-2 tracking-wider">
              Rules
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section data-track-section="hero" className="relative min-h-screen flex items-center justify-center">
      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        {/* Logo */}
        <div className="mb-8">
          <img 
            src={LOGO_URL} 
            alt="Fallen Dragon Events" 
            className="w-48 md:w-64 mx-auto drop-shadow-2xl"
            style={{ filter: 'drop-shadow(0 0 30px rgba(0,0,0,0.8))' }}
          />
        </div>
        
        <h1 className="text-4xl md:text-6xl font-bold tracking-wider mb-4">
          <span className="text-white drop-shadow-lg" style={{ 
            textShadow: '0 0 40px rgba(255,255,255,0.2), 0 4px 20px rgba(0,0,0,0.8)' 
          }}>
            FALLEN DRAGON
          </span>
          <span className="block text-xl md:text-2xl font-light text-gray-400 mt-4 tracking-[0.4em]">
            E V E N T S
          </span>
        </h1>
        
        <div className="mt-8 mb-16">
          <p className="text-lg md:text-xl text-gray-300 tracking-[0.1em] mb-2">
            Airsoft Milsim & LARP Events
          </p>
          <p className="text-base md:text-lg text-gray-500 tracking-[0.05em]">
            Built for those who want immersion.
          </p>
        </div>
        
        {/* Scroll indicator - centered */}
        <div className="flex flex-col items-center">
          <button 
            onClick={() => {
              const element = document.getElementById('events');
              if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            data-testid="enter-darkness-btn"
            className="inline-block px-10 py-3 bg-transparent border border-white/40 text-white text-sm font-medium tracking-[0.2em] transition-all duration-300 hover:bg-white/10 hover:border-white/60"
          >
            ENTER THE DARKNESS
          </button>
          <svg className="w-6 h-6 text-white/30 mt-8 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>
    </section>
  );
};

// About/Experience Section
const AboutSection = () => {
  return (
    <section id="about" data-track-section="about" className="relative py-12">
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white tracking-[0.15em] mb-4"
            style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}>
          THE EXPERIENCE
        </h2>
        <div className="w-24 h-px bg-gradient-to-r from-transparent via-red-800/50 to-transparent mx-auto mb-8" />
        
        <p className="text-lg text-gray-300 leading-relaxed mb-6 max-w-2xl mx-auto">
          Something is waiting for you in these woods. We just haven't told you what yet.
        </p>
        
        <p className="text-lg text-gray-400 leading-relaxed mb-12 max-w-2xl mx-auto">
          Hundreds of acres of North Welsh woodland. Villages. Roads. Buildings. And an experience built to stay with you long after you leave.
        </p>

        <div className="grid md:grid-cols-3 gap-6 mt-12">
          <div className="p-6 bg-black/30 backdrop-blur-sm rounded-lg border border-white/5">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-black/40 border border-red-900/20 flex items-center justify-center">
              <svg className="w-7 h-7 text-red-700/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-white font-medium mb-2 tracking-wider text-sm">COMMUNITY</h3>
            <p className="text-gray-500 text-sm">
              Join a welcoming community where everyone belongs. No egos, just teamwork, laughter, and shared victories.
            </p>
          </div>
          
          <div className="p-6 bg-black/30 backdrop-blur-sm rounded-lg border border-white/5">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-black/40 border border-red-900/20 flex items-center justify-center">
              <svg className="w-7 h-7 text-red-700/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            </div>
            <h3 className="text-white font-medium mb-2 tracking-wider text-sm">ADVENTURE AWAITS</h3>
            <p className="text-gray-500 text-sm">
              Scenario-driven Milsim and LARP where every player is important. Strategic objectives meet epic fun and cinematic moments.
            </p>
          </div>
          
          <div className="p-6 bg-black/30 backdrop-blur-sm rounded-lg border border-white/5">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-black/40 border border-red-900/20 flex items-center justify-center">
              <svg className="w-7 h-7 text-red-700/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
            </div>
            <h3 className="text-white font-medium mb-2 tracking-wider text-sm">TOTAL IMMERSION</h3>
            <p className="text-gray-500 text-sm">
              Step into a living world where the only limit is your imagination. Atmospheric settings, epic lore, and a place where anything is possible.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Events Section
const EventsSection = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${API}/booking/events`);
      const data = await response.json();
      if (data.success) {
        setEvents(data.events);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short',
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    }).toUpperCase();
  };

  return (
    <section id="events" data-track-section="events" className="relative py-12">
      <div className="relative z-10 max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-[0.15em] mb-4"
              style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}>
            UPCOMING OPERATIONS
          </h2>
          <div className="w-24 h-px bg-gradient-to-r from-transparent via-red-800/50 to-transparent mx-auto mb-6" />
          <p className="text-gray-400 max-w-xl mx-auto tracking-wide">
            Choose your mission. Enter the unknown.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-10 h-10 border-2 border-gray-700 border-t-red-800 rounded-full animate-spin"></div>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-16 bg-black/30 backdrop-blur-sm rounded-lg border border-white/5">
            <p className="text-gray-400 text-lg">No upcoming events scheduled</p>
            <p className="text-gray-600 mt-2">The darkness awaits...</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {events.map((event) => (
              <div 
                key={event.id}
                onClick={() => navigate(`/event/${event.id}`)}
                data-testid={`event-card-${event.id}`}
                className="bg-black/40 backdrop-blur-sm rounded-lg overflow-hidden border border-white/5 hover:border-red-900/30 transition-all duration-500 group cursor-pointer"
              >
                {/* Event Image */}
                {event.image_url ? (
                  <div className="h-44 overflow-hidden bg-black/30 flex items-center justify-center">
                    <img 
                      src={getImageUrl(event.image_url)} 
                      alt={event.name}
                      className="max-w-full max-h-full object-contain opacity-70 group-hover:opacity-90 transition-all duration-500"
                    />
                  </div>
                ) : (
                  <div className="h-44 bg-gradient-to-br from-gray-900/50 to-black/50 flex items-center justify-center">
                    <svg className="w-14 h-14 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  </div>
                )}
                
                <div className="p-5">
                  <h3 className="text-lg font-bold text-white mb-3 tracking-wide group-hover:text-red-500/80 transition-colors">
                    {event.name}
                  </h3>
                  
                  <div className="space-y-2 text-sm text-gray-400 mb-4">
                    <p className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-red-800/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {event.end_date && event.end_date !== event.date
                        ? `${formatDate(event.date)} - ${formatDate(event.end_date)}`
                        : formatDate(event.date)
                      }
                    </p>
                    <p className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-red-800/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {event.location}
                    </p>
                  </div>

                  {/* Availability */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-red-500 font-bold text-lg">
                      £{event.price?.toFixed(2)}
                    </span>
                    <span className={`text-sm font-medium ${event.spots_available > 0 ? 'text-gray-400' : 'text-red-500'}`}>
                      {event.spots_available > 0 
                        ? `${event.spots_available} spots`
                        : 'MISSION FULL'
                      }
                    </span>
                  </div>

                  <div
                    className={`w-full py-2.5 text-sm font-medium tracking-[0.15em] transition-all duration-300 rounded text-center ${
                      event.spots_available > 0
                        ? 'bg-transparent border border-white/20 text-white group-hover:bg-red-900/20 group-hover:border-red-800/50'
                        : 'bg-red-900/30 text-red-400 border border-red-800/50 hover:bg-red-900/50'
                    }`}
                  >
                    {event.spots_available > 0 ? 'DEPLOY NOW' : 'JOIN WAITLIST'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// Gallery Section - Carousel
const GallerySection = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await fetch(`${API}/gallery`);
      const data = await response.json();
      if (data.success) {
        setImages(data.images);
      }
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-advance carousel
  useEffect(() => {
    if (images.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [images.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  if (loading) {
    return (
      <section id="gallery" className="relative py-24">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="inline-block w-10 h-10 border-2 border-gray-700 border-t-red-800 rounded-full animate-spin"></div>
        </div>
      </section>
    );
  }

  if (images.length === 0) {
    return null;
  }

  return (
    <section id="gallery" data-track-section="gallery" className="relative py-12">
      <div className="relative z-10 max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-[0.15em] mb-4"
              style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}>
            FROM THE FIELD
          </h2>
          <div className="w-24 h-px bg-gradient-to-r from-transparent via-red-800/50 to-transparent mx-auto mb-6" />
          <p className="text-gray-400 max-w-xl mx-auto tracking-wide">
            Moments captured from our immersive operations.
          </p>
        </div>

        {/* Carousel */}
        <div className="relative">
          {/* Image Title - Above the image */}
          {images[currentIndex]?.title && (
            <div className="text-center mb-4">
              <p className="text-white text-xl md:text-2xl font-medium">
                {images[currentIndex].title}
              </p>
            </div>
          )}
          
          {/* Main Image */}
          <div 
            className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden rounded-lg cursor-pointer bg-black/30"
            onClick={() => setSelectedImage(images[currentIndex])}
          >
            <img 
              src={getImageUrl(images[currentIndex]?.image_url)}
              alt={images[currentIndex]?.alt_text || images[currentIndex]?.title || 'Gallery image'}
              className="w-full h-full object-contain transition-all duration-700"
            />
          </div>

          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/40 hover:bg-black/60 rounded-full text-white/70 hover:text-white transition-all"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/40 hover:bg-black/60 rounded-full text-white/70 hover:text-white transition-all"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Dots indicator */}
          {images.length > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-1.5 rounded-full transition-all ${
                    index === currentIndex 
                      ? 'bg-red-700/80 w-8' 
                      : 'bg-white/20 w-1.5 hover:bg-white/40'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Thumbnail strip */}
          {images.length > 1 && (
            <div className="flex gap-2 mt-6 overflow-x-auto pb-2 justify-center">
              {images.map((image, index) => (
                <button
                  key={image.id}
                  onClick={() => setCurrentIndex(index)}
                  className={`flex-shrink-0 w-16 h-12 md:w-24 md:h-16 rounded overflow-hidden transition-all ${
                    index === currentIndex 
                      ? 'ring-2 ring-red-700/60 opacity-100' 
                      : 'opacity-40 hover:opacity-70'
                  }`}
                >
                  <img 
                    src={getImageUrl(image.image_url)}
                    alt={image.alt_text || image.title || ''}
                    className="w-full h-full object-contain"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2"
            onClick={() => setSelectedImage(null)}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img 
            src={getImageUrl(selectedImage.image_url)}
            alt={selectedImage.alt_text || selectedImage.title || 'Gallery image'}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {selectedImage.title && (
            <div className="absolute bottom-4 left-0 right-0 text-center">
              <p className="text-white text-lg">{selectedImage.title}</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" data-track-section="contact" className="relative py-12">
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white tracking-[0.15em] mb-4">
          CONTACT HQ
        </h2>
        <div className="w-24 h-px bg-gradient-to-r from-transparent via-red-800/50 to-transparent mx-auto mb-8" />
        
        <p className="text-gray-400 mb-8 max-w-xl mx-auto">
          Questions about events, group bookings, or private operations? Reach out.
        </p>

        <a 
          href="mailto:info@fallendragonevents.co.uk"
          className="inline-flex items-center text-gray-300 hover:text-white transition-colors text-lg"
        >
          <svg className="w-6 h-6 mr-3 text-red-800/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          info@fallendragonevents.co.uk
        </a>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="relative py-10 border-t border-white/5">
      <div className="relative z-10 max-w-6xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center">
            <img src={LOGO_URL} alt="Fallen Dragon" className="h-8 w-auto mr-4 opacity-50" />
            <span className="text-gray-600 text-sm">
              © {new Date().getFullYear()} Fallen Dragon Events
            </span>
          </div>
          
          {/* Social Links */}
          <div className="flex items-center space-x-4">
            <a 
              href="https://instagram.com/fallen.dragon.events" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-pink-400 transition-colors"
              aria-label="Instagram"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a 
              href="https://www.facebook.com/groups/566788218031740" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-blue-400 transition-colors"
              aria-label="Facebook"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a 
              href="https://www.tiktok.com/@ottxfd" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-white transition-colors"
              aria-label="TikTok"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
              </svg>
            </a>
          </div>
          
          <div className="flex items-center space-x-6">
            <Link to="/general-rules" className="text-gray-600 hover:text-gray-400 text-sm transition-colors">
              General Rules
            </Link>
            <Link to="/privacy-policy" className="text-gray-600 hover:text-gray-400 text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-gray-600 hover:text-gray-400 text-sm transition-colors">
              Terms
            </Link>
            <Link 
              to="/admin/login" 
              className="text-gray-700 hover:text-gray-500 text-xs transition-colors"
            >
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Home Component with Seamless Background
const Home = () => {
  // Initialize section tracking
  useSectionTracking();
  
  // Track page view on mount
  useEffect(() => {
    trackPageView();
  }, []);
  
  return (
    <div className="relative min-h-screen">
      {/* Fixed Background Layer - Single Continuous Forest */}
      <div className="fixed inset-0 z-0">
        {/* Base forest image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${FOREST_BG})` }}
        />
        
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-black/50" />
        
        {/* Mist/fog overlay for atmosphere */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, transparent 20%, transparent 80%, rgba(0,0,0,0.4) 100%)'
          }}
        />
        
        {/* Subtle vignette */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.4) 100%)'
          }}
        />
        
        {/* Subtle noise texture overlay */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
          }}
        />
      </div>
      
      {/* Content Layer */}
      <div className="relative z-10">
        <Navigation />
        <HeroSection />
        
        {/* Soft transition mist */}
        <div className="h-32 bg-gradient-to-b from-transparent to-black/20 pointer-events-none" />
        
        <AboutSection />
        
        {/* Soft transition */}
        <div className="h-24 bg-gradient-to-b from-black/20 via-transparent to-black/20 pointer-events-none" />
        
        <EventsSection />
        
        {/* Soft transition */}
        <div className="h-24 bg-gradient-to-b from-black/20 via-transparent to-black/20 pointer-events-none" />
        
        <GallerySection />
        
        {/* Soft transition */}
        <div className="h-24 bg-gradient-to-b from-black/20 via-transparent to-black/30 pointer-events-none" />
        
        <ContactSection />
        
        <Footer />
      </div>
    </div>
  );
};

export default Home;
